import { useEffect, useState } from 'react';
import getSessionStatus from './SessionStatus';

type UseSessionStatusProps = {
  /** Session Start Time */
  start: Date | string;
  /** Session End Time */
  end: Date | string;
  /** Interval to check session status in milliseconds. Defaults to 30 seconds. */
  refreshRate?: number;
  /** Additional flags to return particular session statuses */
  options?: {
    /** Flag to return the detailed 'before' session statuses if set to true, i.e. MORE_THAN_TEN_MIN_BEFORE and LESS_THAN_TEN_MIN_BEFORE. Defaults to false. */
    useDetailedBeforeStatus?: boolean;
    /** Flag to return the detailed 'during' session statuses if set to true, i.e. LESS_THAN_FIVE_MIN_BEFORE_END and LESS_THAN_ONE_MIN_BEFORE_END. Defaults to false. */
    useDetailedDuringStatus?: boolean;
    /** Flag to return the detailed 'after' session statuses if set to true, i.e. LESS_THAN_ONE_HOUR_AFTER and MORE_THAN_ONE_HOUR_AFTER. Defaults to false. */
    useDetailedAfterStatus?: boolean;
  };
};

/**
 * Hook to periodically check the current session status.
 */
const useSessionStatus = ({ start, end, refreshRate = 30000, options = {} }: UseSessionStatusProps): string => {
  const [status, setStatus] = useState(getSessionStatus(new Date(Date.now()), start, end, options));

  const { useDetailedBeforeStatus = false, useDetailedDuringStatus = false, useDetailedAfterStatus = false } = options;

  useEffect(() => {
    const getNewStatus = () => {
      const newStatus = getSessionStatus(new Date(Date.now()), start, end, {
        useDetailedBeforeStatus,
        useDetailedDuringStatus,
        useDetailedAfterStatus
      });

      if (newStatus !== status) {
        setStatus(newStatus);
      }
    };

    getNewStatus();

    // Check session status at an interval
    const interval = setInterval(getNewStatus, refreshRate);

    // Cleanup interval
    return () => clearInterval(interval);
  }, [status, start, end, refreshRate, useDetailedBeforeStatus, useDetailedDuringStatus, useDetailedAfterStatus]);

  return status;
};

export default useSessionStatus;
